import { LINK_TYPES } from '../config/entityTypeRegistry';
import { createAccommodationReferenceBlock } from '../utils/contentBlockUtils';
import { getAccommodationById, updateAccommodation } from '../firebase/firestoreService';
import { isAccommodationDayItineraryBlock } from '../utils/linkExtractorUtils';
import { shouldWriteLegacy } from '../utils/linkRegistryModeUtils';
import { linkRegistryConfigService } from './linkRegistryConfigService';

function findEndpoint(linkDoc, entityType) {
  if (linkDoc.entityLow?.type === entityType) {
    return linkDoc.entityLow;
  }
  if (linkDoc.entityHigh?.type === entityType) {
    return linkDoc.entityHigh;
  }
  return null;
}

function getOtherEndpoint(linkDoc, entityType) {
  if (linkDoc.entityLow?.type === entityType) {
    return linkDoc.entityHigh;
  }
  if (linkDoc.entityHigh?.type === entityType) {
    return linkDoc.entityLow;
  }
  return null;
}

async function loadAccommodationService() {
  const module = await import('@trip-planner-monorepo/accommodation');
  return module.accommodationService;
}

async function loadDaysService() {
  const module = await import('@trip-planner-monorepo/days');
  return module.daysService;
}

async function loadDocumentService() {
  const module = await import('@trip-planner-monorepo/documents');
  return module.documentService;
}

async function loadTravelService() {
  const module = await import('@trip-planner-monorepo/travel');
  return module.travelService;
}

async function loadEntityLinkingService() {
  const module = await import('./entityLinkingService');
  return module.entityLinkingService;
}

export const legacyLinkAdapter = {
  /**
   * @param {string} userId
   * @param {string} tripId
   * @param {Object} linkDoc
   */
  async writeLegacyLink(userId, tripId, linkDoc) {
    const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
    if (!shouldWriteLegacy(config)) {
      return { skipped: true, reason: 'writeMode not dual_write' };
    }

    switch (linkDoc.linkType) {
    case LINK_TYPES.LOCATED_IN:
      return this._writeLocatedIn(userId, tripId, linkDoc);
    case LINK_TYPES.STAYS_ON:
      return this._writeStaysOn(userId, tripId, linkDoc);
    case LINK_TYPES.DOCUMENT_FOR:
      return this._writeDocumentFor(userId, tripId, linkDoc);
    case LINK_TYPES.DEPARTS_FROM:
    case LINK_TYPES.ARRIVES_AT:
      return this._writeTravelPlaceField(userId, tripId, linkDoc);
    case LINK_TYPES.ASSOCIATED_WITH:
      return this._writeAssociatedWith(userId, tripId, linkDoc);
    default:
      console.warn(`[legacyLinkAdapter] No legacy writer for linkType ${linkDoc.linkType}`);
      return { skipped: true, reason: 'unsupported linkType' };
    }
  },

  /**
   * @param {string} userId
   * @param {string} tripId
   * @param {Object} linkDoc
   */
  /**
   * Removes legacy embedded references for a registry link (registry_only hygiene).
   * Does not check writeMode — safe when registry is the source of truth.
   */
  async stripLegacyShadow(userId, tripId, linkDoc) {
    switch (linkDoc.linkType) {
    case LINK_TYPES.LOCATED_IN:
      return this._removeLocatedIn(userId, tripId, linkDoc);
    case LINK_TYPES.STAYS_ON:
      return this._removeStaysOn(userId, tripId, linkDoc);
    case LINK_TYPES.DOCUMENT_FOR:
      return this._removeDocumentFor(userId, tripId, linkDoc);
    case LINK_TYPES.DEPARTS_FROM:
    case LINK_TYPES.ARRIVES_AT:
      return this._removeTravelPlaceField(userId, tripId, linkDoc);
    case LINK_TYPES.ASSOCIATED_WITH:
      return this._removeAssociatedWith(userId, tripId, linkDoc);
    default:
      return { skipped: true, reason: 'unsupported linkType' };
    }
  },

  async removeLegacyLink(userId, tripId, linkDoc) {
    const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
    if (!shouldWriteLegacy(config)) {
      return { skipped: true, reason: 'writeMode not dual_write' };
    }

    switch (linkDoc.linkType) {
    case LINK_TYPES.LOCATED_IN:
      return this._removeLocatedIn(userId, tripId, linkDoc);
    case LINK_TYPES.STAYS_ON:
      return this._removeStaysOn(userId, tripId, linkDoc);
    case LINK_TYPES.DOCUMENT_FOR:
      return this._removeDocumentFor(userId, tripId, linkDoc);
    case LINK_TYPES.DEPARTS_FROM:
    case LINK_TYPES.ARRIVES_AT:
      return this._removeTravelPlaceField(userId, tripId, linkDoc);
    case LINK_TYPES.ASSOCIATED_WITH:
      return this._removeAssociatedWith(userId, tripId, linkDoc);
    default:
      return { skipped: true, reason: 'unsupported linkType' };
    }
  },

  async _writeLocatedIn(userId, tripId, linkDoc) {
    const accommodation = findEndpoint(linkDoc, 'accommodation');
    const place = findEndpoint(linkDoc, 'place');
    if (!accommodation || !place) {
      throw new Error('located_in link requires accommodation and place endpoints');
    }
    const accommodationService = await loadAccommodationService();
    await accommodationService.updateAccommodationPlaceReference(
      userId,
      tripId,
      accommodation.id,
      place.id
    );
    return { success: true };
  },

  async _removeLocatedIn(userId, tripId, linkDoc) {
    const accommodation = findEndpoint(linkDoc, 'accommodation');
    if (!accommodation) {
      return { skipped: true };
    }
    const accommodationService = await loadAccommodationService();
    await accommodationService.unlinkAccommodationFromPlace(userId, tripId, accommodation.id);
    return { success: true };
  },

  async _writeStaysOn(userId, tripId, linkDoc) {
    const accommodation = findEndpoint(linkDoc, 'accommodation');
    const day = findEndpoint(linkDoc, 'day');
    if (!accommodation || !day) {
      throw new Error('stays_on link requires accommodation and day endpoints');
    }

    const accommodationEntity = await getAccommodationById(userId, tripId, accommodation.id);
    const accommodationName = accommodationEntity?.details?.name
      || accommodationEntity?.summary
      || 'Accommodation';

    const contentBlock = createAccommodationReferenceBlock(
      accommodation.id,
      accommodationName,
      linkDoc.displayContext || null
    );

    const daysService = await loadDaysService();
    await daysService.addAccommodationContentBlock(userId, tripId, day.id, contentBlock);
    return { success: true };
  },

  async _removeStaysOn(userId, tripId, linkDoc) {
    const accommodation = findEndpoint(linkDoc, 'accommodation');
    const day = findEndpoint(linkDoc, 'day');
    if (!accommodation || !day) {
      return { skipped: true };
    }
    const daysService = await loadDaysService();
    await daysService.removeAccommodationContentBlocks(userId, tripId, day.id, accommodation.id);

    const accommodationEntity = await getAccommodationById(userId, tripId, accommodation.id);
    const existingBlocks = accommodationEntity?.contentBlocks || [];
    const updatedBlocks = existingBlocks.filter(
      (block) => !(isAccommodationDayItineraryBlock(block) && block.dayId === day.id)
    );
    if (updatedBlocks.length !== existingBlocks.length) {
      await updateAccommodation(userId, tripId, accommodation.id, {
        contentBlocks: updatedBlocks,
        contentBlocksLastUpdated: new Date().toISOString()
      });
    }
    return { success: true };
  },

  async _writeDocumentFor(userId, tripId, linkDoc) {
    const document = findEndpoint(linkDoc, 'document');
    const entity = document ? getOtherEndpoint(linkDoc, 'document') : null;
    if (!document || !entity) {
      throw new Error('document_for link requires document and entity endpoints');
    }
    const documentService = await loadDocumentService();
    await documentService.addConnection(userId, tripId, document.id, {
      entityType: entity.type,
      entityId: entity.id,
      connectionType: linkDoc.metadata?.connectionType || 'related'
    });
    return { success: true };
  },

  async _removeDocumentFor(userId, tripId, linkDoc) {
    const document = findEndpoint(linkDoc, 'document');
    const entity = document ? getOtherEndpoint(linkDoc, 'document') : null;
    if (!document || !entity) {
      return { skipped: true };
    }
    const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
    const documentService = await loadDocumentService();
    // Strip embedded legacy only — never archive registry rows during parity repair.
    await documentService.removeConnection(userId, tripId, document.id, entity.type, entity.id, {
      legacyCleanupOnly: true,
      suppressEvent: true
    });
    return { success: true };
  },

  async _writeTravelPlaceField(userId, tripId, linkDoc) {
    const travel = findEndpoint(linkDoc, 'travel');
    const place = findEndpoint(linkDoc, 'place');
    if (!travel || !place) {
      throw new Error('travel place link requires travel and place endpoints');
    }
    const fieldName = linkDoc.linkType === LINK_TYPES.DEPARTS_FROM ? 'departurePlace' : 'arrivalPlace';
    const travelService = await loadTravelService();
    await travelService.updateTravel(userId, tripId, travel.id, {
      [fieldName]: place.id,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  },

  async _removeTravelPlaceField(userId, tripId, linkDoc) {
    const travel = findEndpoint(linkDoc, 'travel');
    if (!travel) {
      return { skipped: true };
    }
    const fieldName = linkDoc.linkType === LINK_TYPES.DEPARTS_FROM ? 'departurePlace' : 'arrivalPlace';
    const travelService = await loadTravelService();
    await travelService.updateTravel(userId, tripId, travel.id, {
      [fieldName]: null,
      updatedAt: new Date().toISOString()
    });
    return { success: true };
  },

  async _writeAssociatedWith(userId, tripId, linkDoc) {
    const entityLinkingService = await loadEntityLinkingService();
    const source = linkDoc.entityLow;
    const target = linkDoc.entityHigh;
    await entityLinkingService.createLegacyLinkOnly(
      userId,
      tripId,
      source.type,
      source.id,
      target.type,
      target.id,
      {
        bidirectional: true,
        metadata: {
          connectionType: linkDoc.metadata?.connectionType,
          context: linkDoc.displayContext
        }
      }
    );
    return { success: true };
  },

  async _removeAssociatedWith(userId, tripId, linkDoc) {
    const entityLinkingService = await loadEntityLinkingService();
    const source = linkDoc.entityLow;
    const target = linkDoc.entityHigh;
    await entityLinkingService.removeLegacyLinkOnly(
      userId,
      tripId,
      source.type,
      source.id,
      target.type,
      target.id
    );
    return { success: true };
  }
};

export default legacyLinkAdapter;
